import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { makeStyles } from 'transfix-ui/core'
import { useGlobalToast } from 'transfix-ui'
import { ICarrierPageFragment } from '../../../Carrier/graphql/CarrierPageFragment'
import { CarrierRelationshipsModalForm } from './CarrierRelationshipsModalForm'
import { TEST_IDS } from './constants'
import { useSetCarrierRelationshipsMutation } from './graphql/SetCarrierRelationships'
import { getDefaultValues } from './helpers/getDefaultValues'
import { prepareSetCarrierRelationshipsInput } from './helpers/prepareSetCarrierRelationshipsInput'
import { useCarrierRelationshipsModalForm } from './hooks/useCarrierRelationshipsModalForm'

export interface ICarrierRelationshipsModalProps extends Omit<IModalBaseProps, 'modalType'> {
  carrierApplication: ICarrierPageFragment
}

const useCarrierRelationshipsModalStyles = makeStyles({
  body: {
    padding: 24,
  },
  modal: {},
  header: {},
  title: {},
  root: {},
})

export const CarrierRelationshipsModal = ({
  carrierApplication,
  onClose,
  open = false,
  ...props
}: ICarrierRelationshipsModalProps) => {
  const classes = useCarrierRelationshipsModalStyles()
  const { openToast } = useGlobalToast()

  const { carrierRelationships } = carrierApplication

  const methods = useCarrierRelationshipsModalForm({
    defaultValues: useMemo(() => getDefaultValues(carrierRelationships), [carrierRelationships]),
  })
  const { handleSubmit, reset } = methods

  // Reset the form's default values when the carrier's relationships updates
  useEffect(() => reset(getDefaultValues(carrierRelationships)), [carrierRelationships, reset])

  const [setCarrierRelationships, { loading }] = useSetCarrierRelationshipsMutation()

  const closeHandler = useCallback(() => {
    onClose()
    reset()
  }, [onClose, reset])

  const saveHandler = handleSubmit(async data => {
    try {
      const response = await setCarrierRelationships({
        variables: {
          input: prepareSetCarrierRelationshipsInput(data, carrierApplication),
        },
      })

      const errors = response.data?.setCarrierRelationships?.errors?.map(e => e.message)

      if (errors && errors.length > 0) {
        openToast({
          toastMessage: errors.join(',\n'),
          toastType: 'alert',
        })
      } else {
        closeHandler()
        openToast({
          toastMessage: 'Successfully updated team for carrier.',
        })
      }
    } catch (err) {
      openToast({
        toastMessage: err?.message,
        toastType: 'alert',
      })
    }
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: closeHandler,
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      disabled: loading,
      action: saveHandler,
      type: 'confirm',
      dataTest: TEST_IDS.confirmBtn,
    },
  ]

  return (
    <ModalBase
      dataTest='CarrierRelationshipsModal'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={actions}
      className={classes.root}
      bodyClassName={classes.body}
      title='Edit Managers'
      titleClass={classes.title}
      modalClassName={classes.modal}
      headerClassName={classes.header}
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <CarrierRelationshipsModalForm />
      </FormProvider>
    </ModalBase>
  )
}
